'use client';

import { AuthUser, IAuthUser, PhoneNumber } from '@order-up/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User as FirebaseUser, updateEmail, updateProfile, type MultiFactorResolver } from 'firebase/auth';
import { useEffect } from 'react';
import { getFirebaseAuth } from '../firebase/config';
import { clearSelectionStorage } from '../utils/selectionStorage';
import { adminApiService } from '../services/adminApi';
import { authService, mapFirebaseUserToAuthUser } from '../services/auth';
import { getActiveGuestSession, subscribeGuestSession } from '../services/guestSession';
import { broadcastAuthChange, setupCrossAppAuthListener } from '../services/crossAppAuth';
import { userService } from '../services/users';
import { createLogger } from '../utils/logger';
import { errorHandler } from '../utils/errorHandler';
import { refreshSessionCookie } from '../utils/sessionRefresh';
import { clearPersistedQueryCache } from '../utils/queryCacheStorage';
import { userKeys } from './useUsers';

const logger = createLogger('useAuth');

/**
 * Detect a sign-in failure whose root cause is Firebase App Check attestation
 * (token rejected by the backend, or an `appCheck/`-namespaced SDK failure).
 * These are an outage class distinct from ordinary credential errors, so we
 * route them to monitoring separately. Detected structurally by error code.
 */
export const isAppCheckAuthFailure = (error: unknown): boolean => {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code !== 'string') {
    return false;
  }
  return (
    code === 'auth/firebase-app-check-token-is-invalid' ||
    code.startsWith('appCheck/') ||
    code.startsWith('app-check/')
  );
};

/**
 * Defines the query keys used for authentication-related data.
 * This ensures consistency when accessing the query cache.
 */
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

/**
 * The auth cache payload.
 *
 * `authResolved` is the load-bearing field. Firebase restores a persisted
 * session asynchronously (IndexedDB read, sometimes an ID-token refresh), so
 * for the first few hundred milliseconds of every cold load `authUser` is
 * `null` for a signed-IN user. Without a separate "has the listener answered
 * yet?" bit, `authUser === null` is ambiguous between *signed out* and *not
 * known yet* — and every guard resolved that ambiguity as "signed out" and
 * redirected to the auth app, which promptly signed the user back in and
 * bounced them home. That double page-load IS the "why am I being logged out
 * and back in" complaint.
 *
 * React Query's own `isLoading` cannot express this: `initialData` makes the
 * query `success` at t=0, so `isLoading` is permanently `false`.
 */
export interface AuthSnapshot {
  authUser: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  /** True once `onAuthStateChanged` has fired at least once. */
  authResolved: boolean;
  /**
   * A storefront guest whose browser App Check refused: there is no Firebase
   * user on this page, but the API minted a real Firebase anonymous user for
   * it and the page runs on that uid's guest session (services/guestSession.ts).
   * `null` whenever a Firebase user exists. Read the guest's uid through
   * `customerUidOf` (utils/customerUid.ts), never by reaching into the session.
   */
  guestUid?: string | null;
}


/**
 * Module-level latch mirroring `authResolved`. The React Query cache can be
 * cleared or invalidated by unrelated code (profile updates, phone linking,
 * onboarding — see the `invalidateQueries(authKeys.user())` call sites); this
 * latch survives that so a re-run of `queryFn` can rebuild an honest snapshot
 * instead of reporting "not resolved yet" forever.
 */
let hasAuthResolved = false;

/** Build the current snapshot from live Firebase state. */
const readAuthSnapshot = (): AuthSnapshot => {
  try {
    const current = getFirebaseAuth().currentUser;
    return {
      authUser: current ? mapFirebaseUserToAuthUser(current) : null,
      firebaseUser: current,
      authResolved: hasAuthResolved,
      guestUid: current ? null : (getActiveGuestSession()?.uid ?? null),
    };
  } catch {
    // Firebase not initialised yet (this hook can render a tick before
    // AppProviders' init effect lands). Nothing is known.
    return { authUser: null, firebaseUser: null, authResolved: false };
  }
};

/**
 * A hook to get the current authentication state of the user.
 *
 * This hook is the source of truth for the user's authentication status.
 * It's powered by TanStack Query and updated by the `useAuthSubscription` hook.
 *
 * **`isLoading` means "Firebase has not answered yet."** It is overridden below
 * to track `authResolved` rather than React Query's own fetch state, because
 * the raw value was a trap: `initialData` makes this query `success` at t=0, so
 * `isLoading` was permanently `false` and every consumer that wrote the
 * perfectly reasonable `if (isLoading) return;` got no protection at all. That
 * silently broke five call sites across four apps in exactly the same way —
 * redirecting or reading auth before the persisted session had been restored.
 * Rather than leave a footgun exported and fix each caller, the field now means
 * what everyone already assumed it meant.
 *
 * `authResolved` on the data remains available for callers that prefer to be
 * explicit; the two are always consistent.
 *
 * @returns A TanStack Query result object containing the {@link AuthSnapshot}.
 */
export const useAuth = () => {
  const query = useQuery<AuthSnapshot>({
    queryKey: authKeys.user(),
    // Reads LIVE Firebase state rather than returning a hardcoded null pair.
    // This used to be `async () => ({ authUser: null, firebaseUser: null })`,
    // which meant any `invalidateQueries(authKeys.user())` — fired by profile
    // updates, phone linking and onboarding — refetched the query and wiped a
    // signed-in user's auth state to signed-out, tripping the guards into a
    // redirect. Rebuilding from `auth.currentUser` makes invalidation a no-op
    // at worst and self-healing at best.
    queryFn: async () => readAuthSnapshot(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false, // The subscription handles this.
    // Explicit initial data keeps the query out of React Query's loading state;
    // `authResolved` is the real signal, surfaced as `isLoading` below.
    initialData: () => readAuthSnapshot(),
  });

  return {
    ...query,
    isLoading: !query.data.authResolved,
  };
};


/**
 * A subscription hook that listens to Firebase auth state changes in real-time.
 * This should be used once at the root of the component tree (e.g., in providers).
 *
 * Enhanced with automatic sync between Firebase Auth and Firestore User documents
 * and cross-app authentication state synchronization.
 */
export const useAuthSubscription = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const auth = getFirebaseAuth();
    let lastKnownUser: IAuthUser | null = null;

    // Set up cross-app auth synchronization
    let crossAppCleanup: (() => void) | null = null;

    const setupCrossAppSync = async () => {
      try {
        crossAppCleanup = setupCrossAppAuthListener();
        logger.debug('Cross-app auth sync enabled');
      } catch (error) {
        logger.warn('Failed to setup cross-app auth sync', { error: error instanceof Error ? error.message : String(error) });
      }
    };

    setupCrossAppSync();

    // Listen for cross-app signout events and clear cache
    const handleCrossAppSignout = () => {
      logger.debug('Cross-app signout detected, clearing query cache and selection');
      queryClient.clear();
      // The on-disk slice must go too, or the next user on this browser could
      // hydrate the previous one's cached data.
      clearPersistedQueryCache();
      // This tab is signed out by a broadcast from another tab, so the
      // useCrossAppSignOut mutation's onSuccess never runs here. Sweep the
      // persisted selection directly so it can't leak into the next user's
      // session on a shared browser.
      clearSelectionStorage();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('cross-app-signout-complete', handleCrossAppSignout);
    }

    /**
     * Background reconciliation between Firebase Auth profile fields and the
     * Firestore User document. Runs detached from the auth listener so the
     * React Query cache update isn't gated on a Firestore round-trip — see
     * the `void runUserAutoSync(...)` call inside `onAuthStateChanged`.
     */
    const runUserAutoSync = async (
      user: IAuthUser,
      previousUser: IAuthUser | null,
    ): Promise<void> => {
      // Only sync on meaningful changes — not on plain token refreshes.
      if (
        previousUser &&
        previousUser.uid === user.uid &&
        previousUser.email === user.email &&
        previousUser.displayName === user.displayName &&
        previousUser.photoURL === user.photoURL &&
        previousUser.emailVerified === user.emailVerified
      ) {
        return;
      }

      logger.debug('Detected meaningful auth changes, syncing to Firestore');

      try {
        // Check if user document exists first - handles the sign-up race condition.
        const existingUser = await userService.getUserByUid(user.uid).catch(() => null);

        if (!existingUser) {
          logger.debug('Skipping auto-sync - user document does not exist yet (normal during sign-up)');
          return;
        }

        logger.debug('Auto-sync: Firestore → Firebase Auth');

        // Sync FROM Firestore TO Firebase Auth (Firestore is the source of truth
        // for displayName / photoURL / email).
        const authSyncData: { displayName?: string | null; photoURL?: string | null; email?: string } = {};
        let shouldSyncAuth = false;

        if (existingUser.displayName !== user.displayName) {
          authSyncData.displayName = existingUser.displayName;
          shouldSyncAuth = true;
          logger.debug('Syncing displayName from Firestore to Firebase Auth',
            { from: user.displayName, to: existingUser.displayName });
        }

        if (existingUser.photoURL !== user.photoURL) {
          authSyncData.photoURL = existingUser.photoURL;
          shouldSyncAuth = true;
          logger.debug('Syncing photoURL from Firestore to Firebase Auth',
            { from: user.photoURL, to: existingUser.photoURL });
        }

        if (existingUser.email !== user.email) {
          authSyncData.email = existingUser.email;
          shouldSyncAuth = true;
          logger.debug('Syncing email from Firestore to Firebase Auth',
            { from: user.email, to: existingUser.email });
        }

        if (shouldSyncAuth) {
          try {
            const auth = getFirebaseAuth();
            const currentUser = auth.currentUser;
            if (currentUser) {
              if (authSyncData.email) {
                await updateEmail(currentUser, authSyncData.email);
                logger.debug('Email synced to Firebase Auth', { email: authSyncData.email });
              }

              const profileData: { displayName?: string | null; photoURL?: string | null } = {};
              if ('displayName' in authSyncData) profileData.displayName = authSyncData.displayName;
              if ('photoURL' in authSyncData) profileData.photoURL = authSyncData.photoURL;

              if (Object.keys(profileData).length > 0) {
                await updateProfile(currentUser, profileData);
              }

              await currentUser.reload();
              logger.debug('Firebase Auth profile synced from Firestore');
            }
          } catch (syncError) {
            logger.warn('Failed to sync Firestore data to Firebase Auth', { error: syncError instanceof Error ? syncError.message : String(syncError) });
          }
        }

        // Only sync emailVerified from Firebase Auth to Firestore (one-way).
        if (user.emailVerified !== existingUser.emailVerified) {
          logger.debug('Syncing emailVerified to Firestore', { emailVerified: user.emailVerified });
          await userService.updateUser(user.uid, { emailVerified: user.emailVerified });
        }

        // E1 · keep the multi-verified-email set in sync with Firebase
        // providerData. Fires after a meaningful auth change while the login is
        // fresh enough for the endpoint's step-up gate. Best-effort and
        // non-fatal: a network error or a 409 (the email is already claimed by
        // another account) must never block sign-in or the rest of auto-sync.
        if (user.emailVerified) {
          void authService.syncVerifiedEmails().catch((syncError) => {
            logger.debug('Verified-email sync skipped (non-fatal)', {
              error: syncError instanceof Error ? syncError.message : String(syncError),
            });
          });
        }

        logger.debug('Auto-sync completed for existing user');
      } catch (error) {
        logger.warn('Auto-sync failed (non-fatal)', { error: error instanceof Error ? error.message : String(error) });
      }
    };

    const unsubscribe = auth.onAuthStateChanged(
      (user: IAuthUser | null) => {
        logger.debug('Auth state changed', { hasUser: !!user });
        const authUser = user ? mapFirebaseUserToAuthUser(user as FirebaseUser) : null;

        // Critical path: update the auth cache synchronously so consumers can
        // render immediately. Previously this was gated behind
        // `await getCurrentUserToken()` (only used in a debug log), which
        // serialized a token-refresh round-trip in front of first paint.
        //
        // The listener firing at all is what makes `authUser` trustworthy:
        // until this runs, `null` means "don't know yet", not "signed out".
        hasAuthResolved = true;
        queryClient.setQueryData<AuthSnapshot>(authKeys.user(), {
          authUser,
          firebaseUser: user as FirebaseUser | null,
          authResolved: true,
          guestUid: user ? null : (getActiveGuestSession()?.uid ?? null),
        });

        // Background reconciliation — fire-and-forget so the listener returns
        // immediately. Subscribers see the new auth state without waiting on
        // `getUserByUid` / `updateEmail` / `updateProfile`.
        if (user) {
          const previousUser = lastKnownUser;
          lastKnownUser = user;
          if (user !== previousUser) {
            void runUserAutoSync(user, previousUser);
          }
        } else {
          lastKnownUser = null;
          // Signed out via the listener (token expiry, admin revocation, or a
          // sign-out that didn't go through the mutation hooks). Drop any
          // persisted tenant/restaurant selection so it can't leak into the
          // next user's session on a shared browser. Safe on a logged-out
          // initial load too — onAuthStateChanged only fires null on a genuine
          // signed-out state, never transiently for an authenticated user.
          clearSelectionStorage();
        }
      },
      (error: Error) => {
        logger.error('Auth state error', error);
        // The listener errored — Firebase will not tell us who the user is.
        // Resolve as signed-out so guards stop waiting; an indefinite hold
        // would be a worse failure than a redirect to sign-in.
        hasAuthResolved = true;
        queryClient.setQueryData<AuthSnapshot>(authKeys.user(), {
          authUser: null,
          firebaseUser: null,
          authResolved: true,
          guestUid: getActiveGuestSession()?.uid ?? null,
        });
      }
    );

    // A guest session activating (or ending) changes who the guest is on this
    // page without any Firebase event, so it updates the snapshot itself.
    const unsubscribeGuestSession = subscribeGuestSession(() => {
      queryClient.setQueryData<AuthSnapshot>(authKeys.user(), (prev) => ({
        ...(prev ?? readAuthSnapshot()),
        guestUid: prev?.firebaseUser ? null : (getActiveGuestSession()?.uid ?? null),
      }));
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribe();
      unsubscribeGuestSession();
      if (crossAppCleanup) {
        crossAppCleanup();
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('cross-app-signout-complete', handleCrossAppSignout);
      }
    };
  }, [queryClient]);
};

/**
 * Hook for Google sign-in
 */
/**
 * Hook to manually trigger sync from Firestore to Firebase Auth
 * Call this after updating user data in Firestore to ensure Firebase Auth stays in sync
 */
export const useSyncFirestoreToAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uid: string) => {
      const auth = getFirebaseAuth();
      const currentUser = auth.currentUser;

      if (!currentUser || currentUser.uid !== uid) {
        throw new Error('Cannot sync: user not authenticated or UID mismatch');
      }

      // Get latest Firestore data
      const firestoreUser = await userService.getUserByUid(uid);
      if (!firestoreUser) {
        throw new Error('User not found in Firestore');
      }

      // Compare and sync differences
      const authSyncData: { displayName?: string | null; photoURL?: string | null; email?: string } = {};
      let shouldSync = false;

      if (firestoreUser.displayName !== currentUser.displayName) {
        authSyncData.displayName = firestoreUser.displayName;
        shouldSync = true;
      }

      if (firestoreUser.photoURL !== currentUser.photoURL) {
        authSyncData.photoURL = firestoreUser.photoURL;
        shouldSync = true;
      }

      if (firestoreUser.email !== currentUser.email) {
        authSyncData.email = firestoreUser.email;
        shouldSync = true;
      }

      if (shouldSync) {
        // Handle email separately
        if (authSyncData.email) {
          await updateEmail(currentUser, authSyncData.email);
        }

        // Update profile fields
        const profileData: { displayName?: string | null; photoURL?: string | null } = {};
        if ('displayName' in authSyncData) profileData.displayName = authSyncData.displayName;
        if ('photoURL' in authSyncData) profileData.photoURL = authSyncData.photoURL;

        if (Object.keys(profileData).length > 0) {
          await updateProfile(currentUser, profileData);
        }

        await currentUser.reload();
        logger.debug('Manual sync completed');
      }

      return shouldSync;
    },
    onSuccess: (didSync) => {
      if (didSync) {
        // Force refresh auth state to update the query cache
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
      }
    }
  });
};

export const useGoogleSignIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const firebaseUser = await authService.signInWithGoogle();

      // DON'T create user profile yet - let the completion flow handle it
      // Just return the Firebase user for now
      return firebaseUser;
    },
    onSuccess: async (user: AuthUser) => {
      logger.debug('Google sign-in success', { uid: user.uid });
      // The auth subscription will handle auth state updates automatically
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(user.uid) });
    },
    onError: async (error: any) => {
      // Don't log error for user cancellation
      if (error.message !== 'POPUP_CANCELLED') {
        logger.error('Google sign-in error', error);
      }
      // No need to refresh auth state - let the subscription handle it naturally
    },
  });
};

/**
 * Hook for Facebook sign-in
 */
export const useFacebookSignIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const firebaseUser = await authService.signInWithFacebook();

      // DON'T create user profile yet - let the completion flow handle it
      // Just return the Firebase user for now
      return firebaseUser;
    },
    onSuccess: async (user: AuthUser) => {
      logger.debug('Facebook sign-in success', { uid: user.uid });
      // The auth subscription will handle auth state updates automatically
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(user.uid) });
    },
    onError: async (error: any) => {
      // Don't log error for user cancellation
      if (error.message !== 'POPUP_CANCELLED') {
        logger.error('Facebook sign-in error', error);
      }
      // No need to refresh auth state - let the subscription handle it naturally
    },
  });
};

/**
 * Hook for Apple sign-in
 */
export const useAppleSignIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const firebaseUser = await authService.signInWithApple();

      // DON'T create user profile yet - let the completion flow handle it
      // Just return the Firebase user for now
      return firebaseUser;
    },
    onSuccess: async (user: AuthUser) => {
      logger.debug('Apple sign-in success', { uid: user.uid });
      // The auth subscription will handle auth state updates automatically
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(user.uid) });
    },
    onError: async (error: any) => {
      // Don't log error for user cancellation
      if (error.message !== 'POPUP_CANCELLED') {
        logger.error('Apple sign-in error', error);
      }
      // No need to refresh auth state - let the subscription handle it naturally
    },
  });
};

/**
 * A hook for signing in a user with email and password.
 *
 * This is a TanStack Query mutation. It handles the async sign-in process
 * and provides state for loading, errors, and success.
 * On success, it updates the user query data in the cache.
 *
 * @returns A mutation object from TanStack Query. Call `mutate({ email, password })` to trigger the sign-in.
 */
export const useSignIn = () => {
  return useMutation({
    mutationFn: ({ email, password }: Record<string, string>) => authService.signIn(email, password),
    /**
     * On successful sign-in, we don't need to manually update the cache.
     * The `onAuthStateChanged` listener from `useAuthSubscription` will handle it.
     */
    onSuccess: () => {
      logger.debug('Sign-in success. Auth state change will trigger cache update.');
    },
    onError: (error) => {
      logger.error('Sign-in failed', error);
      // App-Check-related sign-in failures are an attestation outage class,
      // not a user credential problem — route them to monitoring distinctly.
      // The mutation still surfaces `mutation.error` unchanged to the form.
      if (isAppCheckAuthFailure(error)) {
        errorHandler.handleError(
          error,
          { component: 'useAuth', operation: 'signIn' },
          { logLevel: 'error' }
        );
      }
    }
  });
};

/**
 * Resolve a sign-in interrupted by the MFA challenge (Phase B3). Pair with
 * `getMfaResolver(error)` from the `useSignIn` error and a collected TOTP code.
 * Success flows through the same `onAuthStateChanged` cache update as `useSignIn`.
 */
export const useResolveMfaSignIn = () => {
  return useMutation({
    mutationFn: ({
      resolver,
      code,
    }: {
      resolver: MultiFactorResolver;
      code: string;
    }) => authService.resolveMfaSignIn(resolver, code),
    onSuccess: () => {
      logger.debug('MFA challenge resolved. Auth state change will update cache.');
    },
  });
};

/**
 * Hook for anonymous sign-in (guest checkout)
 */
export const useSignInAnonymously = () => {
  return useMutation({
    mutationFn: () => authService.signInAnonymously(),
    /**
     * On successful anonymous sign-in, the auth subscription handles the cache update.
     */
    onSuccess: () => {
      logger.debug('Anonymous sign-in success. Guest user session created.');
    },
    onError: (error) => {
      logger.error('Anonymous sign-in failed', error);
    }
  });
};

/**
 * A hook for signing up a new user with email and password.
 *
 * On success, it updates the user query data and sends a verification email.
 *
 * @returns A mutation object. Call `mutate({ email, password, firstName, lastName, phoneNumber })` to trigger the sign-up.
 */
export const useSignUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      skipVerificationEmail,
    }: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phoneNumber?: PhoneNumber;
      /** The invitation flow: accepting verifies the address (see `authService.signUp`). */
      skipVerificationEmail?: boolean;
    }) => {
      // Step 1: Create the user in Firebase Authentication
      const firebaseUser = await authService.signUp(email, password, firstName, lastName, {
        skipVerificationEmail,
      });

      // Step 2: Create the user profile — ON THE SERVER.
      //
      // This used to be a BROWSER Firestore write (`createUserProfile`), and
      // the two steps were UNLINKED: nothing joined them and nothing repaired a
      // failure. Lose step 2 — App Check cold start, a permission-denied, a
      // network blip, the user closing the tab — and the account is
      // PERMANENTLY profile-less. The auth app then dead-ends on
      // AUTH_PROFILE_UNAVAILABLE forever (its "Try again" can never succeed,
      // because the document will never appear on its own), and the entire
      // post-signup ops chain silently never runs either: `apps/functions`
      // moved off the v1 `auth.user().onCreate` trigger onto a v2 Firestore
      // trigger on `users/{uid}`, on the stated assumption that "the
      // user-creation flow already writes users/{uid}".
      //
      // The server write does not depend on browser App Check at all, and the
      // same endpoint HEALS an account that already lost its profile — so this
      // is one mechanism, not a special case bolted onto sign-up.
      const { user: newUser } = await userService.provisionMyProfile({
        firstName,
        lastName,
        ...(phoneNumber ? { phoneNumber } : {}),
      });

      // Prime the user query cache with the profile we just wrote (mirrors
      // useSocialAuthCompletion). `onAuthStateChanged` fires the moment the
      // Auth user is created — before the Firestore doc lands — so a
      // useGetUserByUid query started in that window caches `null` and, with
      // the 5-minute staleTime, serves it long after the doc exists. The
      // direct service call performs no cache updates itself, so this is the
      // only place the race gets closed.
      queryClient.setQueryData(userKeys.detailByUid(newUser.uid), newUser);
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(newUser.uid) });

      return firebaseUser;
    },
    onSuccess: (_data: IAuthUser) => {
      logger.debug('Full sign-up process successful. User cache primed; auth state listener updates auth cache.');
    },
    onError: (error) => {
      logger.error('Full sign-up process failed', error);
    },
  });
};

/**
 * A hook for signing out the current user.
 *
 * On success, it clears the user from the cache and invalidates all other
 * queries to ensure no stale data from the previous session remains.
 * This is a critical cleanup step for security and data integrity.
 *
 * @returns A mutation object. Call `mutate()` to trigger sign-out.
 */
export const useSignOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // 1. Sign out of Firebase immediately — this is the critical path.
      //    Fast, reliable, makes useAuth() return null on next render.
      await authService.signOut();
      // 2. Best-effort server session cleanup — fire and forget.
      //    Don't block the mutation on an API call that may not apply
      //    (e.g. auth app has no server session).
      adminApiService.signOut().catch(() => {});
    },
    onSuccess: () => {
      logger.debug('Sign-out success. Clearing all query caches and selection.');
      queryClient.clear();
      // Drop any persisted tenant/restaurant selection so it can't leak into the
      // next user's session on a shared browser.
      clearSelectionStorage();
    },
    onError: (error) => {
      logger.error('Sign-out failed', error);
    }
  });
};

/**
 * A hook for signing out the current user across all apps.
 *
 * This variant broadcasts the signout event to other apps/tabs to ensure
 * consistent authentication state across the entire platform.
 *
 * @returns A mutation object. Call `mutate()` to trigger cross-app sign-out.
 */
export const useCrossAppSignOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      broadcastAuthChange('signout'); // Notify other tabs
      // 1. Sign out of Firebase immediately — critical path
      await authService.signOut();
      // 2. Best-effort server session cleanup — fire and forget
      adminApiService.signOut().catch(() => {});
    },
    onSuccess: () => {
      logger.debug('Cross-app sign-out success. Clearing all query caches and selection.');
      queryClient.clear();
      // Drop any persisted tenant/restaurant selection so it can't leak into the
      // next user's session on a shared browser.
      clearSelectionStorage();
    },
    onError: (error) => {
      logger.error('Cross-app sign-out failed', error);
    }
  });
};

/**
 * A hook for sending a password reset email to a user.
 *
 * @returns A mutation object. Call `mutate(email)` to send the email.
 */
export const useSendPasswordResetEmail = () => {
  return useMutation({
    // An address, or `{ email, locale }` from a form that renders in the
    // reader's language (the reset email and page then follow it).
    mutationFn: (request: string | { email: string; locale?: string }) =>
      typeof request === 'string'
        ? authService.sendPasswordResetEmail(request)
        : authService.sendPasswordResetEmail(request.email, request.locale),
    onSuccess: () => {
      logger.debug('Password reset email sent successfully');
    },
    onError: (error) => {
      logger.error('Failed to send password reset email', error);
    }
  });
};

/**
 * A hook for deleting the currently authenticated user from Firebase Auth.
 * This can only be used for the current user due to Firebase Auth limitations.
 *
 * @returns A mutation object. Call `mutate()` to delete the current user.
 */
export const useDeleteCurrentUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authService.deleteCurrentUser,
    onSuccess: () => {
      logger.debug('Current user deleted successfully. Clearing all query caches.');
      // Clear all queries since the user is deleted
      queryClient.clear();
    },
    onError: (error) => {
      logger.error('Failed to delete current user', error);
    }
  });
};

/**
 * A hook for updating the current user's Firebase Auth profile.
 *
 * @returns A mutation object. Call `mutate({ displayName?, photoURL? })` to update the profile.
 */
export const useUpdateUserProfile = () => {
  return useMutation({
    mutationFn: (updates: { displayName?: string; photoURL?: string }) =>
      authService.updateUserProfile(updates),
    onSuccess: () => {
      logger.debug('User profile updated successfully. Auth state will be updated automatically.');
      // The auth state change listener will automatically update the cache
      // We don't need to manually update here
    },
    onError: (error) => {
      logger.error('Failed to update user profile', error);
    }
  });
};

/**
 * A hook for sending a verification email to a target user.
 *
 * Pass the target user's UID when calling `mutate(userId)` / `mutateAsync(userId)`.
 * Callers may target themselves (self-service) or, if they are admins, any user.
 * Authorization is enforced server-side.
 *
 * @returns A mutation object. Call `mutate(userId)` to send the email.
 */
export const useSendVerificationEmail = () => {
  return useMutation({
    mutationFn: (userId: string) => authService.sendVerificationEmail(userId),
    onSuccess: () => {
      logger.debug('Verification email sent successfully');
    },
    onError: (error) => {
      logger.error('Failed to send verification email', error);
    }
  });
};

/**
 * Hook to check email verification status
 * Forces a reload of the current user to get the latest verification state
 */
export const useCheckEmailVerification = () => {

  return useMutation({
    mutationFn: async () => {
      const isVerified = await authService.checkEmailVerificationStatus();
      return isVerified;
    },
    onSuccess: (isVerified: boolean) => {
      logger.debug('Email verification status checked', { isVerified });
      // No need to invalidate auth cache - the auth subscription handles auth state changes
      // and user document updates will be handled by the setUserEmailVerified mutation
    },
    onError: (error) => {
      logger.error('Failed to check email verification', error);
    },
  });
};

/**
 * Hook to change the current user's password — the industry-standard,
 * secure flow (replaces the old server endpoint that never verified the
 * current password):
 *   1. `reauthenticateWithPassword(currentPassword)` proves the user knows
 *      their existing password (Firebase rejects a wrong one with
 *      `auth/wrong-password`).
 *   2. `updatePassword(newPassword)` sets the new password.
 *   3. `refreshSessionCookie()` re-mints the server session cookie from the
 *      fresh ID token so the server reflects the new `auth_time`.
 *
 * To sign out OTHER devices after a change, the UI offers a separate
 * "sign out everywhere" action (revokes all refresh tokens). Only show this
 * hook's form for accounts that have a password provider — see
 * `hasPasswordProvider`.
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      await authService.reauthenticateWithPassword(currentPassword);
      if (await authService.isAccountChangeRoutedThroughApi()) {
        // SOC 2 R74: the server makes the change and tells the account.
        // Re-mint the session first so the server sees this fresh sign-in.
        await refreshSessionCookie();
        await authService.changePasswordThroughApi(currentPassword, newPassword);
        // The server signed out every session, this one included. Sign this
        // one back in with the new password. If that needs more than a
        // password (a second step), sign out: the next screen is sign-in,
        // which is what "every device was signed out" means here too.
        try {
          await authService.reauthenticateWithPassword(newPassword);
          await refreshSessionCookie();
        } catch (resignInError) {
          logger.warn('Signed out after a password change; sign in again with the new password', {
            error: resignInError instanceof Error ? resignInError.message : String(resignInError),
          });
          await authService.signOut();
        }
        return;
      }
      await authService.updatePassword(newPassword);
      await refreshSessionCookie();
    },
    onSuccess: () => {
      logger.debug('Password changed successfully');
    },
    onError: (error) => {
      logger.error('Password change failed', error);
    },
  });
};

/**
 * Hook to change the current user's email via verify-before-update — the
 * login email switches ONLY after the user clicks the link sent to the NEW
 * address, so a typo can't lock them out (unlike the old immediate Admin-SDK
 * email swap). For password accounts, pass `currentPassword` to reauthenticate
 * first (Firebase requires a recent login).
 */
export const useChangeEmail = () => {
  return useMutation({
    mutationFn: async ({ newEmail, currentPassword }: { newEmail: string; currentPassword?: string }) => {
      if (currentPassword) {
        await authService.reauthenticateWithPassword(currentPassword);
      }
      if (await authService.isAccountChangeRoutedThroughApi()) {
        // SOC 2 R74: our mailer sends the link, and the server tells the
        // CURRENT address before the change can land.
        if (currentPassword) await refreshSessionCookie();
        await authService.requestEmailChangeThroughApi(newEmail);
        return;
      }
      await authService.verifyBeforeUpdateEmail(newEmail);
    },
    onSuccess: () => {
      logger.debug('Verification email sent for email change');
    },
    onError: (error) => {
      logger.error('Email change failed', error);
    },
  });
};

export interface SocialSignUpData {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  /** Skip sending verification email (e.g. invited users — acceptance verifies the email) */
  skipVerificationEmail?: boolean;
}

/**
 * Hook to complete social auth signup by creating user document and sending verification email
 */
export const useSocialAuthCompletion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SocialSignUpData) => {
      // Get current Firebase Auth user
      const auth = getFirebaseAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      if (!currentUser.email) {
        throw new Error('User email not found');
      }

      // Create the user profile in Firestore
      const phonePayload: PhoneNumber | undefined = data.phoneNumber
        ? { countryCode: '+1', number: data.phoneNumber }
        : undefined;

      const displayName = `${data.firstName} ${data.lastName}`;

      // Update Firebase Auth profile with display name
      await authService.updateUserProfile({ displayName });

      // Server-side, for the same reason as `useSignUp` above — and for one
      // more that is specific to this path. This form is shown whenever a
      // social account has no `users/{uid}` document, which includes an
      // EXISTING user whose profile write was lost. The old browser write
      // created that user a profile with `assignments: []`, and claims are
      // DERIVED from `assignments` (claimsService.ts:66) — so the next claims
      // resync would have recomputed from that empty array and silently revoked
      // every tenant and restaurant grant they still held. The server
      // reconstructs assignments from the account's own claims instead, so
      // "complete your profile" can no longer be a quiet demotion.
      const { user: newUser } = await userService.provisionMyProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        displayName,
        ...(phonePayload ? { phoneNumber: phonePayload } : {}),
      });

      // Send verification email unless explicitly skipped (e.g. invitation flow
      // where acceptInvitation() will set emailVerified: true server-side).
      // Target is the just-signed-in social user (self-service path).
      if (!data.skipVerificationEmail) {
        await authService.sendVerificationEmail(currentUser.uid);
      }

      return newUser;
    },
    onSuccess: (userData) => {
      logger.debug('Social auth signup completed', { uid: userData.uid });
      // Invalidate and refetch user queries to trigger UI update immediately
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(userData.uid) });
      // Also set the query data immediately to avoid loading states
      queryClient.setQueryData(userKeys.detailByUid(userData.uid), userData);
      // No need to refresh auth state - the subscription handles this naturally
    },
    onError: (error) => {
      logger.error('Social auth signup failed', error);
    },
  });
};

/**
 * Hook for signing in with a custom Firebase token for client SDK sync.
 * Used in hybrid authentication to sync server session with client SDK.
 */
export const useSignInWithToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const userCredential = await authService.signInWithToken(token);
      return mapFirebaseUserToAuthUser(userCredential.user);
    },
    onSuccess: async (user: AuthUser) => {
      logger.debug('Client SDK sync successful', { uid: user.uid });
      // The auth subscription will handle auth state updates automatically
      queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(user.uid) });
    },
    onError: (error: unknown) => {
      logger.error('Client SDK sync failed', error);
    },
  });
};

/**
 * Switch the active tenant context. Re-mints custom claims so that
 * restaurant-scoped grants for the new tenant's restaurants are populated
 * in the ID token. Tenant-scoped grants for ALL tenants remain present
 * regardless of context.
 *
 * After mutation success the token is already refreshed AND the session
 * cookie is re-minted (the service calls `getIdToken(true)` then re-mints the
 * cookie via `/signin` internally) and the auth query is invalidated so React
 * state updates. Pass `rememberMe` to preserve the original session duration.
 */
export const useSwitchTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: string | { tenantDocId: string; rememberMe?: boolean },
    ) => {
      const { tenantDocId, rememberMe } =
        typeof params === 'string' ? { tenantDocId: params, rememberMe: false } : params;
      await authService.switchTenantContext(tenantDocId, rememberMe);
    },
    onSuccess: () => {
      logger.info('Tenant context switched — invalidating auth cache');
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
    onError: (error: unknown) => {
      logger.error('Switch tenant failed', error);
    },
  });
};
